import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { IdbStorage } from "@/store/idbStorage.ts";
import { OrderPaymentMethod } from "@/pages/point-of-sale/models/order-payment-method.model.ts";
import { roundMoney } from "@/utilities/decimal.utils";

export interface IOrderPaymentMethodState {
  orderPaymentMethods: OrderPaymentMethod[];
  currentOrderPaymentMethod: OrderPaymentMethod | undefined;
  addOrderPaymentMethod: (newOrderPaymentMethod: OrderPaymentMethod) => void;
  updateOrderPaymentMethod: (
    updatedOrderPaymentMethod: OrderPaymentMethod
  ) => void;
  deleteOrderPaymentMethod: (uuid: string) => void;
  setCurrentOrderPaymentMethod: (uuid: string) => void;
  getOrderPaymentMethodsAmountSummary: () => number;
  resetOrderPaymentMethods: () => void;
  resetStore: () => void;
}

export const useOrderPaymentMethodStore = create<IOrderPaymentMethodState>()(
  devtools(
    persist(
      (set, get) => ({
        orderPaymentMethods: [],
        currentOrderPaymentMethod: undefined,
        addOrderPaymentMethod: (newOrderPaymentMethod: OrderPaymentMethod) => {
          set(({ orderPaymentMethods }) => ({
            orderPaymentMethods: [
              ...orderPaymentMethods,
              {
                ...newOrderPaymentMethod,
                amount: roundMoney(newOrderPaymentMethod.amount ?? 0),
              },
            ],
          }));
        },
        updateOrderPaymentMethod: (
          updatedOrderPaymentMethod: OrderPaymentMethod
        ) => {
          const orderPaymentMethods = get().orderPaymentMethods;

          const updatedOrderPaymentMethods = orderPaymentMethods?.map(
            (previousOrderPaymentMethod: OrderPaymentMethod) => {
              if (
                previousOrderPaymentMethod.uuid !==
                updatedOrderPaymentMethod.uuid
              ) {
                return previousOrderPaymentMethod;
              }
              return {
                ...updatedOrderPaymentMethod,
                amount: roundMoney(updatedOrderPaymentMethod.amount ?? 0),
                amountReceived:
                  updatedOrderPaymentMethod.amountReceived != null
                    ? roundMoney(updatedOrderPaymentMethod.amountReceived)
                    : undefined,
                change:
                  updatedOrderPaymentMethod.change != null
                    ? roundMoney(updatedOrderPaymentMethod.change)
                    : undefined,
              };
            }
          );
          set(() => ({ orderPaymentMethods: updatedOrderPaymentMethods }));
        },
        deleteOrderPaymentMethod: (uuid: string) => {
          const orderPaymentMethods = get().orderPaymentMethods;
          const updatedOrderPaymentMethods = orderPaymentMethods?.filter(
            (paymentMethod) => paymentMethod.uuid !== uuid
          );
          set(() => ({ orderPaymentMethods: updatedOrderPaymentMethods }));
        },
        setCurrentOrderPaymentMethod: (uuid: string) => {
          const orderPaymentMethods = get().orderPaymentMethods;
          const orderPaymentMethod = orderPaymentMethods?.find(
            (paymentMethod: OrderPaymentMethod) => paymentMethod.uuid === uuid
          );
          set(() => ({ currentOrderPaymentMethod: orderPaymentMethod }));
        },
        getOrderPaymentMethodsAmountSummary: () => {
          const orderPaymentMethods = get().orderPaymentMethods;
          return roundMoney(
            orderPaymentMethods.reduce(
              (totalAmount, paymentMethod) =>
                totalAmount + (paymentMethod.amount ?? 0),
              0
            )
          );
        },
        resetOrderPaymentMethods: () => {
          set(() => ({ orderPaymentMethods: [] }));
        },
        resetStore: () => {
          set(() => ({
            orderPaymentMethods: [],
            currentOrderPaymentMethod: undefined,
          }));
        },
      }),
      {
        name: "payment-method-store",
        partialize: (state) => ({
          orderPaymentMethods: state.orderPaymentMethods,
        }),
        storage: createJSONStorage(() => IdbStorage),
      }
    )
  )
);
